import { kebabCase } from 'lodash';
import { useCallback } from 'react';
import { FilterState } from '../DataTable';
import useDataTableState from './useDataTableState';

type FilterValue = FilterState[string];

/**
 * Drives a DataTable's filter from the outside — for segment bars, deep links and other chrome
 * that lives above the table.
 *
 * It writes into the very store the table hydrates from, so the change arrives without a remount.
 * The obvious alternative, encoding the value in `fetchUrl`, would fork the persisted filter state
 * per variant (the store is keyed by `kebabCase(fetchUrl)`), and the user would lose their filters
 * every time they switch.
 *
 * Two conditions: pass the exact same `fetchUrl` string the DataTable gets, and do **not** give
 * that DataTable an `initialFilterState` — it disables the hydration this relies on.
 */
const useDataTableFilterControl = (fetchUrl: string) => {
    const { filterState, setFilterState } = useDataTableState(kebabCase(fetchUrl))();

    // Sets one filter key and leaves the rest of the user's filters alone.
    const patch = useCallback(
        (key: string, value: FilterValue) => {
            setFilterState({ ...(filterState || {}), [key]: value });
        },
        [filterState, setFilterState]
    );

    return { filterState: filterState || {}, patch, setFilterState };
};

export default useDataTableFilterControl;
